/**
 * Ein Key pro Frage — was nach der Umbenennung noch übersetzt werden muss.
 *
 * ## Wo wir stehen
 *
 * Der PO hat am 18.08.2026 entschieden: **ein Key pro Frage**, und zwar der
 * Key der Langform. Stufe 1 hat 47 eindeutige Paare gesammelt, Stufe 2 hat
 * **36 davon in `schema-kurz.ts` umbenannt** — die Kurzform stellt dieselbe
 * Frage jetzt unter demselben Key wie die Langform.
 *
 * Damit schließt sich das eigentliche Loch: Die Auswertung
 * (`AnalysemodusAuswertungPage`; `AnalysemodusReviewModal` ist die derzeit
 * nicht verdrahtete Dialog-Variante) rendert ausschließlich das
 * Langform-Schema und liest `answers[field.key]`. Vor der Umbenennung war eine
 * Kurzform-Einreichung dort nur für die ~28 ohnehin deckungsgleichen Keys
 * sichtbar; alles andere hat der Berater nie zu sehen bekommen und konnte es
 * folglich auch nicht korrigieren.
 *
 * ## Zwei Tabellen, zwei Bedeutungen
 *
 * Der Unterschied ist absichtlich strukturell und nicht bloß kommentiert:
 *
 *  1. {@link HISTORISCHE_ANTWORT_KEYS} — die 36 umbenannten Paare. Diese Keys
 *     stehen in **keinem** Schema mehr. Sie existieren nur noch in
 *     gespeicherten Antwortsätzen (Einreichungen, Auswertungs-Snapshots,
 *     Portal-Workspaces, localStorage-Entwürfe) und werden beim **Lesen**
 *     aufgelöst — {@link kanonisiereAntworten}. Umbenannt wurde nur, wo `type`,
 *     Optionsliste **und** Format beider Felder übereinstimmen und beide Felder
 *     dieselbe *eine* Frage stellen; die Auflösung ist deshalb an jeder Lese-/
 *     Renderstelle gefahrlos. Die ersten drei Bedingungen prüft der Guard-Test
 *     mechanisch, die vierte kann er nicht sehen — ein Kurzform-Label, das zwei
 *     Fragen zusammenfasst („Wie viele und welcher Umsatzanteil?"), ist am
 *     Fragetext nicht erkennbar. Dagegen hilft nur die Ausschlussliste unten.
 *
 *  2. {@link AKTIVE_ANTWORT_KEY_ALIASSE} — die neun Paare, die **nicht**
 *     umbenannt wurden, weil `type` oder Optionsliste auseinandergehen. Beide
 *     Keys sind weiterhin echte Schemafelder. Ein gemeinsamer Key über zwei
 *     verschiedenen Wertelisten würde Angaben stillschweigend verfälschen
 *     („Eigentum" gegen `eigentum`, Freitext gegen contactPicker), deshalb
 *     bleiben sie getrennt, bis der PO entschieden hat, welche Werteliste gilt.
 *
 * Der Unterschied ist nicht kosmetisch: Die aktiven Aliasse dürfen **nicht**
 * in einen Renderpfad. Würde die Auswertung `st1_eigentumsverhaeltnis:
 * "Eigentum"` auf `standort_eigentum` legen, stünde dort ein Wert, den das
 * Select der Langform (`eigentum` | `miete` | `pacht` | `leasing`) nicht kennt
 * — und das Bestätigen der Auswertung würde ihn festschreiben. Sie sind darum
 * ausschließlich für Verbraucher gedacht, die einen Skalar in eine
 * `varchar`-Spalte schreiben: {@link kanonisiereAntwortenMitAktivenAliassen},
 * heute nur die Stammdaten-Übernahme (`risikoanalyse-stammdaten.ts`).
 *
 * Auch dort ist es kein Freibrief: `handelsregister_art → hr_register` landet
 * in `Contact.register`, und das Kontakt-UI rendert diese Spalte als Dropdown
 * (`RegisterNumberInput`, HRA/HRB/GnR/GsR/PR/VR) — den Kurzform-Wert „Keines"
 * kennt es nicht. Die Spalte ist `varchar` und nimmt ihn an, das Dropdown
 * zeigt ihn nicht. Das ist Bestand (die Zuordnung gab es vor „ein Key pro
 * Frage" schon) und gehört mit in die PO-Runde.
 *
 * ## Was in keiner der beiden Tabellen steht
 *
 * Fälle, in denen ein Kurzform-Feld mehrere Langform-Felder zusammenfasst oder
 * umgekehrt — ein Alias würde dort Angaben verfälschen:
 *
 *  - **Standort 2 und „weitere Standorte"** (`st2_*`, `weitere_standorte`):
 *    Die Langform kennt nur *einen* Standort. Das löst erst die wiederholbare
 *    Standortgruppe (`docs/plan-analyse-wiederholbare-angaben.md`).
 *  - **`st1_strasse`** trägt „Straße & Hausnummer" in einem Feld, die Langform
 *    trennt `standort_strasse` und `standort_hausnummer`.
 *  - **Vermögens-Sammelfragen** (`techn_betriebseinrichtung`,
 *    `kaufm_betriebseinrichtung`, `waren_vorraete`, `grundstuecke`,
 *    `sonstige_vermoegenswerte` samt `_details`): je eine Kurzform-Frage über
 *    mehreren `asset_*`-Fragen der Langform.
 *  - **`eigene_kfz_details` / `fremde_kfz_details`**: ein Freitext („Was und
 *    wie viele?") gegenüber zwei Feldern (`kfz_*_was`, `kfz_*_anzahl`).
 *  - **`subunternehmer_details`** („Wie viele und welcher Umsatzanteil?")
 *    gegenüber `subunternehmer_anzahl` + `subunternehmer_anteil`, und
 *    **`freelancer_details`** („Wie viele und regelmäßig?") gegenüber
 *    `freelancer_anzahl` + `freelancer_aufwand`. Dieselbe Form wie bei den
 *    Kfz-Feldern: ein Freitext über zwei Fragen. Beide waren in Stufe 2
 *    versehentlich umbenannt (auf `subunternehmer_anteil` bzw.
 *    `freelancer_anzahl`) — die Kundenangabe stünde dann unter einer
 *    Beschriftung, die etwas Engeres meint („Umsatzanteil", „Wie viele?"),
 *    während das jeweilige Nachbarfeld leer bleibt. Zurückgedreht.
 *  - **`haftungsrisiko`, `finanz_liquiditaetsrisiko`,
 *    `rechtsstreitigkeitsrisiko`** (je mit `_details`): Sammelfragen ohne
 *    eindeutiges Gegenstück; die Langform zerlegt sie in mehrere Einzelfragen.
 *  - **`unterlagen_hinweis`**: Freitext gegenüber einer Checkbox-Liste.
 *
 * Diese Antworten bleiben unter ihrem eigenen Key erhalten — nichts geht
 * verloren. Sie werden in der Auswertung aber **nicht angezeigt**: Die
 * `AnalysemodusAuswertungPage` rendert ausschließlich Langform-Felder.
 *
 * ## Was die Umbenennung NICHT löst — offene PO-Punkte
 *
 * Vier Dinge, die nach Stufe 2 offen bleiben. Sie stehen hier, weil dieser
 * Kommentar die Vorlage für die PO-Runde ist:
 *
 *  1. **Halb gefüllte Blöcke in der Auswertung.** Ein umbenanntes Ja/Nein-Gate
 *     ist jetzt sichtbar, sein Detailfeld aus der obigen Ausschlussliste nicht
 *     — die Auswertung wertet zudem kein `showWhen`/`showFollowUpWhen` aus und
 *     zeigt jede Zeile. Der Berater liest „Kunde hat den Abschnitt
 *     beantwortet" und hakt bei den leeren Zeilen nicht mehr nach, obwohl die
 *     Angabe im Antwortsatz steht. Betrifft `cyberangriff_ja` (Detail
 *     `cyberrisiko_details`, die Langform hat dafür gar kein Feld),
 *     `kfz_eigene_ja`/`kfz_fremde_ja` (`*_kfz_details`) und die Standort-1-
 *     Adresse (`st1_strasse` gegen `standort_strasse` + `standort_hausnummer`).
 *     Auflösung: entweder das Detailfeld mit zusammenführen bzw. in der
 *     Langform anlegen, oder die Auswertung markiert solche Zeilen als „nur in
 *     der Kurzform beantwortet".
 *  2. **Werteform-Abweichungen über bereits geteilten Keys.** Vollständige
 *     Liste (gemessen über alle 64 geteilten Keys, Test in
 *     `answerKeys.test.ts`): `pruefungspflicht` (Kurzform `text` „(ja/nein)"
 *     gegen `yesno`) und `leiharbeiter_anzahl` (Kurzform ohne Format gegen
 *     `format: "integer"`, dazu ein Freitext über „Mit wie vielen?" +
 *     „Regelmäßig?"). `rechtsform` stand hier als dritter Fall (Kurzform
 *     `select` mit zehn festen Werten gegen freies `text` der Langform) und
 *     ist mit Welle B erledigt: Beide Schemata ziehen jetzt dieselbe Liste aus
 *     `formOptions.ts`. Die verbliebenen zwei bestehen seit jeher und
 *     sind nicht durch die Umbenennung entstanden — aber es ist genau der
 *     Schaden, gegen den die Ausschlussregel der aktiven Aliasse schützt: Ein
 *     in der Kurzform getippter Freitext landet in der Auswertung in einem
 *     Radio-Paar, das ihn nicht kennt, und der erste Klick des Beraters
 *     überschreibt ihn.
 *  3. **`geschaeftsjahr_abweichung` fragt in beiden Modi etwas anderes.** Die
 *     Kurzform fragt die Spanne („Von Monat/Tag bis Monat/Tag", Beispiel
 *     „01.07. – 30.06."), die Langform nur das Ende („Wann endet Ihr
 *     abweichendes Geschäftsjahr?"). `type` und Format sind identisch, es
 *     bricht nichts und der Berater kann korrigieren — aber die Auswertung
 *     zeigt eine Spanne unter einer Frage, die einen Zeitpunkt will, und
 *     schreibt sie so nach `Contact.geschaeftsjahrAbweichend`. Der Fragetext
 *     gehört angeglichen; welcher gilt, ist eine PO-Entscheidung.
 *  4. **Die Scroll-Kurzform bleibt außen vor.** „Ein Key pro Frage" wirkt für
 *     die schrittbasierte Kurzform (`risikoanalyse-kurz`). Die dritte Variante
 *     (`risikoanalyse-kurz-scroll`) legt ihre Antworten als JSON-*String* ab
 *     (`data: JSON.stringify(...)`); die Auswertung gewinnt daraus gar keinen
 *     Antwortsatz und zeigt sie zu 100 % nicht an. Vorbestehend und von der
 *     Umbenennung unberührt — der Link ist aber weiter anbietbar und wird
 *     intern als gleichwertige Auswertungsquelle geführt.
 */
/**
 * Umbenannte Paare: alter Kurzform-Key → heutiger Key.
 *
 * **Nur Lesehilfe für Altdaten.** Kein Feld trägt diese Keys mehr; sie kommen
 * ausschließlich aus gespeicherten Antwortsätzen, die vor der Umbenennung
 * entstanden sind. Nichts darf hier neu dazukommen, ohne dass das Feld in
 * `schema-kurz.ts` gleichzeitig umbenannt wird.
 *
 * Zusicherung, die die Auflösung erlaubt: Kurz- und Langform-Feld hatten
 * denselben `type` und dieselbe Optionsliste (Test in `answerKeys.test.ts`).
 */
export const HISTORISCHE_ANTWORT_KEYS = Object.freeze({
    // ── Gründung, Rechtsform, Steuern ──────────────────────────────────────
    handelsregisternummer: "hr_nummer",
    umsatzsteuernummer: "ust_id",
    vorsteuerabzugsberechtigt: "vorsteuerabzug",
    geschaeftsjahr_abweichend: "geschaeftsjahr_abweichung",
    bilanzierungspflichtig: "bilanzpflicht_ja",
    rechtsvorgaenger: "rechtsvorgaenger_ja",
    beteiligungen_unternehmen: "beteiligungen_ja",
    gesellschafter_beteiligungen: "gesellschafter_beteiligungen_ja",
    veraenderungen_geplant: "veraenderungen_ja",
    gf_versorgung: "gf_versorgung_ja",
    // ── Tätigkeit ──────────────────────────────────────────────────────────
    betriebscharakter: "taetigkeit",
    betriebscharakter_zusatz: "taetigkeit_weiteres",
    // ── Standort 1 (Standort 2 siehe Kopfkommentar) ────────────────────────
    st1_plz: "standort_plz",
    st1_ort: "standort_ort",
    st1_untervermietung: "untervermietung_ja",
    st1_postanschrift: "postanschrift_ja",
    st1_firmensitz: "firmensitz_ja",
    // ── Personal ───────────────────────────────────────────────────────────
    leiharbeiter: "leiharbeiter_ja",
    subunternehmer: "subunternehmer_ja",
    freelancer: "freelancer_ja",
    // `subunternehmer_details` und `freelancer_details` stehen bewusst NICHT
    // hier: ein Freitext über zwei Langform-Feldern (siehe Kopfkommentar).
    bruttojahreslohnsumme: "bg_gesamt",
    // ── Kennzahlen ─────────────────────────────────────────────────────────
    // Die Langform fragt vier Jahre ab (v = vorletztes, l = letztes,
    // a = aktuelles, f = Folgejahr), die Kurzform drei davon.
    umsatz_vorvorjahr: "umsatz_v",
    umsatz_vorjahr: "umsatz_l",
    umsatz_laufend: "umsatz_a",
    wareneinsatz_vorjahr: "wareneinsatz_l",
    rohertrag_vorjahr: "rohertrag_l",
    // ── Risiken ────────────────────────────────────────────────────────────
    umweltrisiko: "umwelt_grundrisiko_ja",
    umweltrisiko_details: "umwelt_grundrisiko_details",
    cyberrisiko: "cyberangriff_ja",
    // ── Waren, Versand, Fahrzeuge ──────────────────────────────────────────
    versand_waren: "versand_ja",
    eigene_kfz: "kfz_eigene_ja",
    fremde_kfz: "kfz_fremde_ja",
    // ── Abschlussfragen ────────────────────────────────────────────────────
    erwartungshaltung: "erwartung_berater",
    besonderheiten_vertraege: "wuensche_vertrag",
    zufriedenheit_bisheriger_makler: "zufriedenheit_vor",
    sonstiges_nachricht: "freitext_kunde",
});
/**
 * Nicht umbenannte Paare: Kurzform-Key → Langform-Key, **mit abweichender
 * Werteform**.
 *
 * Beide Keys sind echte Schemafelder. Die Auflösung ist nur dort erlaubt, wo
 * der Wert als Skalar in eine lose typisierte Spalte wandert
 * ({@link kanonisiereAntwortenMitAktivenAliassen}) — nie beim Rendern.
 *
 * Der `grund` steht bewusst im Code und nicht nur im Kommentar: Diese Liste
 * ist die Vorlage für die PO-Entscheidung, welche Werteliste gilt. Erst danach
 * darf ein Paar hier heraus und in {@link HISTORISCHE_ANTWORT_KEYS} wandern.
 */
export const AKTIVE_ANTWORT_KEY_ALIASSE = Object.freeze({
    handelsregister_art: {
        ziel: "hr_register",
        grund: "Optionslisten weichen ab: die Kurzform kennt zusätzlich „Keines“, " +
            "die Langform nur HRA/HRB/PR/GnR/VR.",
    },
    wirtschaftspruefer: {
        ziel: "wp_buchpruefer",
        grund: "type weicht ab: textarea (Name, Anschrift, Ansprechpartner in einem " +
            "Feld) gegen ein einzeiliges text-Feld der Langform.",
    },
    st1_eigentumsverhaeltnis: {
        ziel: "standort_eigentum",
        grund: "Optionslisten weichen ab: „Eigentum“/„Miete / Pacht / Leasing“ gegen " +
            "eigentum/miete/pacht/leasing. Die Kurzform fasst drei Optionen der " +
            "Langform zu einer zusammen; ein gemeinsamer Key legte einen Wert ins " +
            "Select, den es nicht kennt. (Die Folgefrage nach dem Eigentümer stellen " +
            "beide im selben Fall — Kurzform bei „Miete / Pacht / Leasing“, Langform " +
            "bei notEquals „eigentum“.)",
    },
    st1_eigentuemer: {
        ziel: "standort_eigentuemer",
        grund: "type weicht ab: Freitext gegen contactPicker (Objekt aus contactId " +
            "und label) — ein String im Picker ist kein gültiger Wert.",
    },
    st1_flaeche_gesamt: {
        ziel: "flaeche_gesamt",
        grund: "type weicht ab: number (Spinner-Feld) gegen text. Die Speicherform ist " +
            "auf beiden Seiten ein String (beide Renderer schreiben " +
            "`event.target.value`) und das `format` ist identisch — verschieden ist " +
            "nur das Eingabe-Widget. Zusammenlegbar, sobald der PO entschieden hat, " +
            "welches der beiden die Kurzform künftig zeigt.",
    },
    st1_flaeche_genutzt: {
        ziel: "flaeche_selbst",
        grund: "type weicht ab: number (Spinner-Feld) gegen text. Die Speicherform ist " +
            "auf beiden Seiten ein String (beide Renderer schreiben " +
            "`event.target.value`) und das `format` ist identisch — verschieden ist " +
            "nur das Eingabe-Widget. Zusammenlegbar, sobald der PO entschieden hat, " +
            "welches der beiden die Kurzform künftig zeigt.",
    },
    mitarbeiter_gesamt: {
        ziel: "ma_gesamt",
        grund: "type weicht ab: number (Spinner-Feld) gegen text. Die Speicherform ist " +
            "auf beiden Seiten ein String (beide Renderer schreiben " +
            "`event.target.value`) und das `format` ist identisch — verschieden ist " +
            "nur das Eingabe-Widget. Zusammenlegbar, sobald der PO entschieden hat, " +
            "welches der beiden die Kurzform künftig zeigt.",
    },
    mitarbeiter_gleichzeitig: {
        ziel: "ma_gleichzeitig",
        grund: "type weicht ab: number (Spinner-Feld) gegen text. Die Speicherform ist " +
            "auf beiden Seiten ein String (beide Renderer schreiben " +
            "`event.target.value`) und das `format` ist identisch — verschieden ist " +
            "nur das Eingabe-Widget. Zusammenlegbar, sobald der PO entschieden hat, " +
            "welches der beiden die Kurzform künftig zeigt.",
    },
    bezug_betriebsmittel: {
        ziel: "bezug_mittel",
        grund: "type weicht ab: textarea gegen ein einzeiliges text-Feld.",
    },
});
/** Nur die Ziele der aktiven Aliasse — flache Tabelle für die Auflösung. */
const AKTIVE_ZIELE = Object.freeze(Object.fromEntries(Object.entries(AKTIVE_ANTWORT_KEY_ALIASSE).map(([von, { ziel }]) => [von, ziel])));
/**
 * Der heutige Key zu einem Antwort-Key aus Altdaten. Unbekannte Keys — und die
 * heutigen selbst — kommen unverändert zurück.
 *
 * Löst **nur** umbenannte Keys auf, nicht die aktiven Aliasse.
 */
export const kanonischerKey = (key) => HISTORISCHE_ANTWORT_KEYS[key] ?? key;
/** Gemeinsamer Kern: Antworten entlang einer Key-Tabelle umlegen. */
function umlegen(antworten, tabelle) {
    // Kein Antwortsatz — trotz Typ zur Laufzeit moeglich: Die Scroll-Kurzform
    // legt ihre Antworten als JSON-*String* ab (`data: JSON.stringify(...)`),
    // und die Auswertung reicht `submittedData` ungeprueft herein.
    // `Object.entries("{…}")` ergaebe eine Zeichen-fuer-Zeichen-Tabelle, die der
    // Berater beim Bestaetigen festschreiben wuerde. Lieber leer.
    if (typeof antworten !== "object" || antworten === null || Array.isArray(antworten))
        return {};
    const quelle = antworten;
    const belegt = (v) => v !== null && v !== undefined && !(typeof v === "string" && v.trim() === "");
    const out = {};
    // Erst die heutigen Keys, damit sie den Vorrang behalten.
    for (const [k, v] of Object.entries(quelle)) {
        if (!(k in tabelle))
            out[k] = v;
    }
    for (const [k, v] of Object.entries(quelle)) {
        const ziel = tabelle[k];
        if (!ziel)
            continue;
        if (!belegt(out[ziel]) && belegt(v))
            out[ziel] = v;
    }
    return out;
}
/**
 * Legt Antworten aus Altdaten auf die heutigen Keys um.
 *
 * Das ist die Lese-Kanonisierung: Sie gehört an **jede** Stelle, die einen
 * gespeicherten Antwortsatz lädt und rendert. Ohne sie erscheinen die Eingaben
 * eines vor der Umbenennung angefangenen Formulars als leer — schlimmer als
 * der Zustand vorher. Sie wirkt ausdrücklich auch für Antwortsätze, die
 * zwischen jetzt und dem Deploy noch unter den alten Keys entstehen; ein
 * Umschreiben der Bestandsdaten ist deshalb nicht nötig.
 *
 * Steht eine Angabe schon unter dem heutigen Key, **gewinnt sie** — der alte
 * Key füllt nur Lücken. So kann ein Datensatz beide Schreibweisen enthalten
 * (Übergangszeit), ohne dass die neuere überschrieben wird.
 *
 * Leere Werte (`null`, `undefined`, Leerstring) zählen nicht als belegt: Ein
 * leeres heutiges Feld wird durch den Altwert ersetzt.
 */
export function kanonisiereAntworten(antworten) {
    return umlegen(antworten, HISTORISCHE_ANTWORT_KEYS);
}
/**
 * Wie {@link kanonisiereAntworten}, zusätzlich mit den aktiven Aliassen.
 *
 * **Nicht zum Rendern.** Die aktiven Aliasse führen zwei verschiedene
 * Wertelisten zusammen; wer das Ergebnis in ein Formular legt, schreibt dort
 * Werte fest, die das Feld nicht kennt. Erlaubt ist es nur für Verbraucher,
 * die einen Skalar in eine `varchar`-Spalte schreiben — heute ausschließlich
 * die Stammdaten-Übernahme (`risikoanalyse-stammdaten.ts`, z. B.
 * `handelsregister_art` → Spalte `register`; zum dortigen Dropdown siehe
 * Kopfkommentar).
 */
export function kanonisiereAntwortenMitAktivenAliassen(antworten) {
    return umlegen(antworten, { ...HISTORISCHE_ANTWORT_KEYS, ...AKTIVE_ZIELE });
}
/**
 * Keys von Fragen, die es einmal gab und die aus dem Schema entfernt wurden.
 *
 * Sie gelten weiterhin als **bekannt**, obwohl kein Feld mehr auf sie zeigt.
 * Grund: `validateFormAnswers` meldet jeden Key, den das Schema nicht kennt,
 * als `unknownKeys` — und das Portal lehnt einen Antwortsatz mit solchen Keys
 * rundheraus ab (`form-workspace-service.ts`: „Unknown form field" beim
 * Feld-Update, „Form answers are invalid" beim Abschluss).
 *
 * Ohne diese Liste könnte ein Kunde, der eine inzwischen gestrichene Frage
 * bereits beantwortet hat, sein Formular weder weiter ausfüllen noch abgeben:
 * Der Altwert steckt im gespeicherten Antwortsatz und macht jeden folgenden
 * Schreibvorgang ungültig.
 *
 * Dieselbe Falle gilt für die umbenannten Keys — sie stehen über
 * {@link HISTORISCHE_ANTWORT_KEYS} ebenfalls als bekannt in `validation.ts`
 * und brauchen hier keinen Eintrag.
 *
 * Die Antworten bleiben absichtlich gespeichert (kein Löschen in Bestandsdaten)
 * — sie werden nur nicht mehr angezeigt.
 *
 * Erweitert wird die Liste, wann immer eine Frage aus dem Schema fliegt.
 */
export const ENTFALLENE_ANTWORT_KEYS = new Set([
    // Welle A des Fragen-Deltas, Excel-Zeilen R32/R33/R34 (Delta #10, #11, #12).
    "beteiligungen_details",
    "niederlassungen_ja",
    "niederlassungen_details",
    // Kurzform-Schreibweise der gestrichenen Niederlassungs-Frage.
    "niederlassungen",
]);
